// src/components/messaging/ChatModal.tsx - Updated with profile pictures and navigation
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';

interface Message {
  id: string;
  content: string;
  message_type: string;
  sender: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_picture?: string;
  };
  media_url?: string;
  file_name?: string;
  created_at: string;
  is_edited: boolean;
  reply_to?: {
    id: string;
    content: string;
    sender: string;
  };
}

interface Participant {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  profile_picture?: string;
}

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string | null;
  targetUser?: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_picture?: string;
  };
  onViewUser?: (userId: string) => void; // Add navigation prop
}

export const ChatModal: React.FC<ChatModalProps> = ({
  isOpen,
  onClose,
  conversationId,
  targetUser,
  onViewUser
}) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [headerProfilePicture, setHeaderProfilePicture] = useState<string | null>(null);
  const [conversationData, setConversationData] = useState<any>(null); // Store full conversation data
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && conversationId) {
      fetchMessages();
    }
  }, [isOpen, conversationId]);

  useEffect(() => {
    console.log('=== DEBUGGING PROFILE PICTURE DATA ===');
    console.log('targetUser:', targetUser);
    console.log('conversationData:', conversationData);

    if (conversationData?.participants) {
      console.log('All participants:', conversationData.participants);
      const targetParticipant = conversationData.participants.find((p: Participant) => p.id === targetUser?.id);
      console.log('Target participant found:', targetParticipant);
      console.log('Target participant profile_picture:', targetParticipant?.profile_picture);
    }
  }, [conversationData, targetUser]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (conversationData?.participants && targetUser) {
      const participant = conversationData.participants.find((p: any) => p.id === targetUser.id);
      if (participant?.profile_picture) {
        console.log('Setting header profile picture from API:', participant.profile_picture);
        setHeaderProfilePicture(`https://aztrainersbackend.onrender.com${participant.profile_picture}`);
      }
    }
  }, [conversationData, targetUser]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };



  const getHeaderProfilePicture = () => {
    // First try targetUser prop
    if (targetUser?.profile_picture) {
      // Check if it's already absolute
      if (targetUser.profile_picture.startsWith('http')) {
        return targetUser.profile_picture;
      }
      // Convert relative to absolute
      return `https://aztrainersbackend.onrender.com${targetUser.profile_picture}`;
    }

    // If no targetUser profile picture, check conversation participants (after API loads)
    if (conversationData?.participants && targetUser) {
      const participant = conversationData.participants.find((p: any) => p.id === targetUser.id);
      if (participant?.profile_picture) {
        // Convert relative to absolute
        return `https://aztrainersbackend.onrender.com${participant.profile_picture}`;
      }
    }

    return undefined; // Changed from null to undefined
  };

  const fetchMessages = async () => {
    if (!conversationId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`https://aztrainersbackend.onrender.com/api/conversations/${conversationId}/messages/`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
        setConversationData(data.conversation); // Store conversation data for participant info
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !conversationId || sending) return;

    setSending(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`https://aztrainersbackend.onrender.com/api/conversations/${conversationId}/send/`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: newMessage,
          message_type: 'text'
        })
      });

      if (response.ok) {
        setNewMessage('');
        await fetchMessages(); // Refresh messages
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getFullName = (sender: Message['sender']) => {
    return `${sender.first_name} ${sender.last_name}`.trim() || sender.username;
  };

  // Handle clicking on profile picture to navigate to user profile
  const handleProfileClick = (userId: string) => {
    if (onViewUser) {
      onViewUser(userId);
      onClose(); // Close the modal when navigating
    }
  };

  // Get user profile picture from multiple sources
  const getUserProfilePicture = (userId: string) => {
    let profilePic = undefined;

    // First check if it's the targetUser
    if (targetUser && targetUser.id === userId) {
      profilePic = targetUser.profile_picture;
    }

    // Check if we have this user's profile picture from messages
    if (!profilePic) {
      const messageWithProfile = messages.find(m =>
        m.sender.id === userId && m.sender.profile_picture
      );
      profilePic = messageWithProfile?.sender.profile_picture;
    }

    // Then check conversation participants data
    if (!profilePic && conversationData?.participants) {
      const participant = conversationData.participants.find((p: any) => p.id === userId);
      profilePic = participant?.profile_picture;
    }

    // Filter out invalid values and default placeholder images
    if (!profilePic ||
      profilePic === 'null' ||
      profilePic === 'undefined' ||
      profilePic.trim() === '' ||
      profilePic.includes('/null') ||
      profilePic.includes('/undefined') ||
      profilePic.includes('default-avatar.png') ||  // ← ADD THIS
      profilePic.includes('/static/images/')) {      // ← AND THIS
      return undefined;
    }

    return profilePic;
  };


  if (!isOpen || !conversationId) return null;

  const chatTitle = targetUser ?
    `${targetUser.first_name} ${targetUser.last_name}`.trim() || targetUser.username :
    'Chat';

  return (
    // Full screen overlay with proper z-index
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center">
      {/* Full screen modal container */}
      <div className="bg-white w-full h-full max-w-none max-h-none flex flex-col md:w-[600px] md:h-[700px] md:rounded-lg md:max-w-2xl md:max-h-[90vh]">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0 bg-white">
          <div className="flex items-center space-x-3">
            {targetUser && (
              <button
                onClick={() => handleProfileClick(targetUser.id)}
                className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden hover:opacity-80 transition-opacity"
              >
                {headerProfilePicture || targetUser.profile_picture ? (
                  <img
                    src={headerProfilePicture || (
                      targetUser.profile_picture?.startsWith('http')
                        ? targetUser.profile_picture
                        : `https://aztrainersbackend.onrender.com${targetUser.profile_picture}`
                    )}
                    alt=""
                    className="w-full h-full object-cover"
                    onLoad={() => console.log('Header image loaded successfully')}
                    onError={(e) => {
                      console.error('Header image failed to load');
                      // Reset to show initials
                      setHeaderProfilePicture(null);
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#E6A756] to-[#D2824C] flex items-center justify-center text-white font-bold text-sm">
                    {targetUser.first_name?.[0] || targetUser.username[0]}
                    {targetUser.last_name?.[0] || ''}
                  </div>
                )}
              </button>
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{chatTitle}</h3>
              {targetUser && (
                <p className="text-sm text-gray-500">@{targetUser.username}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors p-2"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {loading ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9CAF88] mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-500">
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((message) => {
              const isOwn = message.sender.id === user?.id;
              const senderProfilePicture = getUserProfilePicture(message.sender.id);

              return (
                <div
                  key={message.id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'} space-x-2`}
                >
                  {/* Show sender profile picture for other users */}
                  {!isOwn && (
                    <button
                      onClick={() => handleProfileClick(message.sender.id)}
                      className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 hover:opacity-80 transition-opacity"
                    >
                      {(() => {
                        console.log('Sender profile picture:', senderProfilePicture); // ADD THIS
                        return senderProfilePicture ? (
                          <img src={senderProfilePicture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-[#E6A756] to-[#D2824C] flex items-center justify-center text-white font-bold text-xs">
                            {message.sender.first_name?.[0] || message.sender.username[0]}
                            {message.sender.last_name?.[0] || ''}
                          </div>
                        );
                      })()}
                    </button>
                  )}

                  <div className={`max-w-xs lg:max-w-md ${isOwn ? 'order-1' : 'order-2'}`}>
                    {/* Sender name for other users */}
                    {!isOwn && (
                      <button
                        onClick={() => handleProfileClick(message.sender.id)}
                        className="text-xs text-gray-600 mb-1 ml-2 hover:text-gray-800 transition-colors"
                      >
                        {getFullName(message.sender)}
                      </button>
                    )}

                    <div className={`px-4 py-2 rounded-lg ${isOwn
                      ? 'bg-[#9CAF88] text-white ml-auto'
                      : 'bg-white text-gray-800 border border-gray-200'
                      }`}>
                      {message.reply_to && (
                        <div className={`text-xs opacity-75 border-l-2 ${isOwn ? 'border-white/30' : 'border-gray-300'
                          } pl-2 mb-1`}>
                          <p className="font-medium">{message.reply_to.sender}</p>
                          <p className="truncate">{message.reply_to.content}</p>
                        </div>
                      )}

                      <p className="text-sm">{message.content}</p>

                      {message.media_url && (
                        <div className="mt-2">
                          {message.message_type === 'image' ? (
                            <img
                              src={message.media_url}
                              alt="Shared image"
                              className="max-w-full rounded"
                            />
                          ) : (
                            <a
                              href={message.media_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="underline"
                            >
                              {message.file_name || 'Download file'}
                            </a>
                          )}
                        </div>
                      )}

                      <p className={`text-xs mt-1 ${isOwn ? 'text-white/75' : 'text-gray-500'
                        }`}>
                        {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* Spacer for own messages to push them right */}
                  {isOwn && <div className="w-8"></div>}
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
          <div className="flex space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:border-[#9CAF88]"
              disabled={sending}
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || sending}
              className="bg-[#9CAF88] text-white rounded-full p-2 hover:bg-[#7A8471] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};